// Vault de archivos (VLT-353 fase 1 · VAU-DOC): los bytes viajan al bucket R2
// a través del API del Worker (/api/vault), autenticado con la sesión REAL de
// Supabase. La descarga la autoriza la BASE con el JWT del solicitante (RLS):
// dueño del ítem o staff de su firma. El original NUNCA se altera (Build Spec).
import { hasBackend, supabase } from '@/lib/supabase';

export interface VaultUploadResult {
  key: string;
  /** Huella calculada en el SERVIDOR sobre los bytes recibidos. */
  sha256: string;
  size: number;
}

async function accessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export const vaultService = {
  /** Sube los bytes reales. null = no se pudo (motivo en errorKey). */
  async upload(file: File): Promise<{ ok: true; result: VaultUploadResult } | { ok: false; errorKey: string }> {
    if (!hasBackend) {
      // Demo: sin red — el flujo se simula aguas arriba (portal.ts).
      return { ok: false, errorKey: 'portal.errors.demoNoVault' };
    }
    const token = await accessToken();
    if (!token) return { ok: false, errorKey: 'auth.errors.generic' };
    try {
      const res = await fetch(`/api/vault/upload?name=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (res.status === 413) return { ok: false, errorKey: 'portal.errors.tooLarge' };
      if (res.status === 415) return { ok: false, errorKey: 'portal.errors.badType' };
      if (res.status === 503) return { ok: false, errorKey: 'portal.errors.vaultOff' };
      if (!res.ok) return { ok: false, errorKey: 'errors.generic.what' };
      return { ok: true, result: (await res.json()) as VaultUploadResult };
    } catch {
      return { ok: false, errorKey: 'errors.generic.what' };
    }
  },

  /** Descarga y entrega al navegador como archivo (con su nombre original).
   *  `pi` (VLT-381): PaymentIntent que desbloquea una descarga con paywall —
   *  el Worker lo verifica contra Stripe antes de entregar los bytes. */
  async download(key: string, fallbackName: string, pi?: string): Promise<boolean> {
    const token = await accessToken();
    if (!token) return false;
    try {
      let res = await fetch(`/api/vault/file?key=${encodeURIComponent(key)}${pi ? `&pi=${encodeURIComponent(pi)}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Step-up (VLT-355): la cuenta tiene MFA pero esta sesión no superó el
      // desafío — el diálogo global lo pide y, elevada la sesión (token aal2
      // nuevo), se reintenta UNA vez.
      if (res.status === 401) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        if (body?.error !== 'step_up_required') return false;
        const { requestStepUp } = await import('@/lib/stepUp');
        if (!(await requestStepUp())) return false;
        const fresh = await accessToken();
        if (!fresh) return false;
        res = await fetch(`/api/vault/file?key=${encodeURIComponent(key)}${pi ? `&pi=${encodeURIComponent(pi)}` : ''}`, {
          headers: { Authorization: `Bearer ${fresh}` },
        });
      }
      if (!res.ok) return false;
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fallbackName || 'documento';
      a.click();
      URL.revokeObjectURL(a.href);
      return true;
    } catch {
      return false;
    }
  },
};
